// Dashboard > Deployment > Undeployed Stores: the managers a vacant store lies under.
//
// A manager's stores are worked out from the people deployed at them, so a store with
// nobody deployed is in no manager's Store Visits. An assignment made on this list says
// whose it is until someone is deployed there; from then on the store follows that
// person's manager again and the assignment is ignored (kept on record, not deleted).
// Since 28 Oct 2026 a store may lie under several people (Trade Heads and AMs): each of
// them, and everyone above each of them, has it.
//
// DashboardPage only wires this up. Here: the one read of the assignments, which rows are
// ticked, and the small pieces the list draws (the cell, the tick boxes, the button).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserCog } from 'lucide-react';
import { storeVacancyManagersAPI } from '../../services/api';
import { storeCodeKey } from './lists/deployment';
import type { StoreVacancyManager, StoreVacancyManagersResponse, VacancyRoleKind } from './storeVacancyManagers';

/** What this reads of one row of the list. */
export interface VacancyListRow {
  /** The row's own id: the first store row of its store code. */
  id: string;
  name: string;
  /** Every store id the row stands for. Rows that share a store code are listed as one. */
  storeIds: string[];
  /**
   * Of `storeIds`, those whose store status is exactly 'Active'. Only an assignment on one
   * of these reaches the manager: the server adds an assigned store to the manager's lists
   * (phone and web) only while it is Active (store-vacancy-managers.service). A row on
   * Notice, say, is still on this list, so its manager is shown as "Not applied". Left
   * out, every store id counts as Active.
   */
  activeStoreIds?: readonly string[];
  /** People still working at the store: nobody is a vacant row, anybody an understaffed one. */
  activeHeadcount?: number;
}

/**
 * The store ids listed under each store code. The list draws the rows that share a code
 * as one, but an assignment is kept per store id: a row's manager goes to all of them.
 * Stores with NO code are also drawn as one row, yet they are unrelated stores: such a row
 * stands only for itself, so "1 store" in the pop-up never means several.
 */
export function storeIdsSharingCode<T extends { id: string; storeCode: string | null | undefined }>(
  stores: readonly T[],
): (store: T) => string[] {
  const byCode = new Map<string, string[]>();
  for (const store of stores) {
    const key = storeCodeKey(store.storeCode);
    const ids = byCode.get(key);
    if (ids) ids.push(store.id);
    else byCode.set(key, [store.id]);
  }
  const noCode = storeCodeKey(null);
  return (store) => {
    const key = storeCodeKey(store.storeCode);
    return key === noCode ? [store.id] : byCode.get(key) ?? [store.id];
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const textOr = <T,>(value: unknown, fallback: T): string | T =>
  (typeof value === 'string' && value.trim() ? value.trim() : fallback);

const kindOr = (value: unknown): VacancyRoleKind | null => (value === 'trade_head' || value === 'am' ? value : null);

/**
 * The assignments out of a GET or PUT answer, or null when the answer is not one (an older
 * server, an error page). Read field by field, so one odd row cannot take the list down.
 */
export const readVacancyManagers = (response: unknown): StoreVacancyManagersResponse | null => {
  const body = isRecord(response) ? response.data : undefined;
  const data = isRecord(body) ? body.data : undefined;
  if (!isRecord(data) || !Array.isArray(data.assignments)) return null;
  const assignments: StoreVacancyManager[] = [];
  for (const row of data.assignments) {
    if (!isRecord(row) || typeof row.storeId !== 'string') continue;
    const managerCode = textOr(row.managerCode, null);
    if (!managerCode) continue;
    assignments.push({
      storeId: row.storeId,
      managerCode,
      managerName: textOr(row.managerName, managerCode),
      managerRole: textOr(row.managerRole, null),
      managerKind: kindOr(row.managerKind),
      managerActive: row.managerActive !== false,
    });
  }
  return { canManage: data.canManage === true, assignments };
};

/** Never rejects: the dashboard must not break because the assignments could not be read. */
const loadVacancyManagers = async (projectId: number): Promise<StoreVacancyManagersResponse | null> => {
  try {
    return readVacancyManagers(await storeVacancyManagersAPI.get(projectId));
  } catch {
    return null;
  }
};

/** True when `month` ("YYYY-MM-01", as the dashboard keeps it) is the month we are in. */
export const isCurrentMonth = (month: string, now: Date = new Date()): boolean =>
  month.slice(0, 7) === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

const isVacant = (row: VacancyListRow): boolean => (row.activeHeadcount ?? 0) === 0;

const KIND_RANK: Record<VacancyRoleKind, number> = { trade_head: 0, am: 1 };
const kindRank = (kind: VacancyRoleKind | null): number => (kind ? KIND_RANK[kind] : 2);
/** Trade Heads first, then AMs, then anyone else, each by name: the order the cell and the pop-up name them in. */
export const byKindThenName = (a: StoreVacancyManager, b: StoreVacancyManager): number =>
  kindRank(a.managerKind) - kindRank(b.managerKind) || a.managerName.localeCompare(b.managerName, 'en', { sensitivity: 'base' });

/** One store's people, as one comparable key (codes without case, sorted). */
const setKey = (managers: readonly StoreVacancyManager[]): string =>
  managers.map((m) => m.managerCode.trim().toLowerCase()).sort().join('|');

const NO_TICKS: ReadonlySet<string> = new Set();

/** What the list draws from, and what its tick boxes, button and pop-up act on. */
export interface VacancyManagerList {
  /** The assignments have been read. Until then, and when they cannot be, the column shows a dash. */
  known: boolean;
  /** The people a row lies under, Trade Heads first. Only a row with nobody deployed has any. */
  managersOf: (row: VacancyListRow) => StoreVacancyManager[];
  /** The first of them (what the column sorts by), or null. */
  managerOf: (row: VacancyListRow) => StoreVacancyManager | null;
  /**
   * The row's manager really has the store in their lists: the assignment is on one of its
   * Active stores. False for a manager on record for a store that is not Active (it reaches
   * nobody until the store is Active again), and for a row with no manager.
   */
  managerApplies: (row: VacancyListRow) => boolean;
  /** This person may assign and the list shows today: the tick boxes and the button are on offer. */
  canAssign: boolean;
  isTicked: (row: VacancyListRow) => boolean;
  toggle: (row: VacancyListRow) => void;
  /** Vacant rows listed (after filters): the ones the header box ticks or clears. */
  vacantCount: number;
  tickedCount: number;
  toggleAll: () => void;
  /** Every store id the ticked rows stand for. */
  tickedStoreIds: string[];
  /** At least one ticked row has a manager, so there is one to remove. */
  tickedHaveManager: boolean;
  /** The people every ticked row shares (the pop-up starts with them), or 'mixed' when the rows differ. */
  tickedCurrent: StoreVacancyManager[] | 'mixed';
  /** The pop-up is open. */
  assigning: boolean;
  openAssign: () => void;
  closeAssign: () => void;
  /** A save went through: close, clear the ticks and show the assignments it answered with. */
  saved: (answer: StoreVacancyManagersResponse | null) => void;
}

export function useStoreVacancyManagers({ shown, ready, projectId, month, asOf, rows }: {
  /** The Undeployed Stores list is on screen. Nothing is asked for anywhere else. */
  shown: boolean;
  /** The rows are this project's own, fully loaded. For a moment after a project switch
   *  the list is still drawn from the roster of the project just left, and nothing may
   *  be ticked from that picture. */
  ready: boolean;
  projectId: number | null;
  /** The month on screen, and the day a past month is shown as of (null for today). */
  month: string;
  asOf: string | null;
  /** The rows listed, after filters. */
  rows: readonly VacancyListRow[];
}): VacancyManagerList {
  // Kept with the project it was read for, so the assignments of the project just left
  // are never shown as this one's.
  const [load, setLoad] = useState<{ projectId: number | null; answer: StoreVacancyManagersResponse | null }>(
    { projectId: null, answer: null },
  );
  const [reads, setReads] = useState(0);
  useEffect(() => {
    if (!shown || !projectId) return undefined;
    let alive = true;
    void loadVacancyManagers(projectId).then((answer) => {
      if (!alive) return;
      // A later read that fails leaves the answer already on screen.
      setLoad((prev) => (answer || prev.projectId !== projectId ? { projectId, answer } : prev));
    });
    return () => { alive = false; };
  }, [shown, projectId, reads]);
  const answer = load.projectId === projectId ? load.answer : null;

  // The server answers with store ids in lower case; matched that way whatever the list holds.
  // One store, several people: kept together, Trade Heads first.
  const byStoreId = useMemo(() => {
    const out = new Map<string, StoreVacancyManager[]>();
    for (const a of answer?.assignments ?? []) {
      const key = a.storeId.toLowerCase();
      const list = out.get(key);
      if (list) {
        if (!list.some((m) => m.managerCode.toLowerCase() === a.managerCode.toLowerCase())) list.push(a);
      } else out.set(key, [a]);
    }
    for (const list of out.values()) list.sort(byKindThenName);
    return out;
  }, [answer]);
  // What is on record is today's assignment. A past month is shown as it stood on its
  // last day, and who was assigned then is not kept, so nothing is shown for it.
  const today = !asOf && isCurrentMonth(month);
  // The row's assignment, one on an Active store first: that is the one that reaches the
  // managers, so it is the one the cell names.
  const assignmentOf = useCallback((row: VacancyListRow): { managers: StoreVacancyManager[]; applies: boolean } | null => {
    // Someone is deployed there: the store follows their manager, whatever is on record.
    if (!today || !isVacant(row)) return null;
    const active = row.activeStoreIds === undefined
      ? null
      : new Set(row.activeStoreIds.map((id) => id.toLowerCase()));
    let notApplied: StoreVacancyManager[] | null = null;
    for (const id of row.storeIds) {
      const assigned = byStoreId.get(id.toLowerCase());
      if (!assigned?.length) continue;
      if (active === null || active.has(id.toLowerCase())) return { managers: assigned, applies: true };
      if (!notApplied) notApplied = assigned;
    }
    return notApplied ? { managers: notApplied, applies: false } : null;
  }, [byStoreId, today]);
  const managersOf = useCallback((row: VacancyListRow) => assignmentOf(row)?.managers ?? [], [assignmentOf]);
  const managerOf = useCallback((row: VacancyListRow) => assignmentOf(row)?.managers[0] ?? null, [assignmentOf]);
  const managerApplies = useCallback((row: VacancyListRow) => assignmentOf(row)?.applies === true, [assignmentOf]);

  // Assigning is for today's vacant stores: a past month is shown as it stood, to read only.
  const canAssign = shown && ready && answer?.canManage === true && today;

  const [ticks, setTicks] = useState<ReadonlySet<string>>(NO_TICKS);
  const [assigning, setAssigning] = useState(false);
  // A filter, the month or the project changed (or the stores were read again): these are
  // no longer the rows the ticks were made on.
  useEffect(() => {
    setTicks(NO_TICKS);
    setAssigning(false);
  }, [projectId, month, rows]);

  const vacantRows = useMemo(() => rows.filter(isVacant), [rows]);
  // Only rows still listed count, so a store that has left the list can never be sent.
  const tickedRows = useMemo(
    () => (canAssign ? vacantRows.filter((row) => ticks.has(row.id)) : []),
    [canAssign, vacantRows, ticks],
  );
  const isTicked = useCallback((row: VacancyListRow) => ticks.has(row.id), [ticks]);
  const toggle = useCallback((row: VacancyListRow) => {
    setTicks((prev) => {
      const next = new Set(prev);
      if (!next.delete(row.id)) next.add(row.id);
      return next;
    });
  }, []);
  const everyTicked = vacantRows.length > 0 && tickedRows.length === vacantRows.length;
  const toggleAll = useCallback(() => {
    setTicks(everyTicked ? NO_TICKS : new Set(vacantRows.map((row) => row.id)));
  }, [everyTicked, vacantRows]);

  const tickedStoreIds = useMemo(
    () => [...new Set(tickedRows.flatMap((row) => row.storeIds))],
    [tickedRows],
  );
  const tickedHaveManager = tickedRows.some((row) => managerOf(row) !== null);
  const tickedCurrent = useMemo((): StoreVacancyManager[] | 'mixed' => {
    const sets = tickedRows.map(managersOf);
    if (sets.length === 0) return [];
    const first = setKey(sets[0]);
    return sets.every((set) => setKey(set) === first) ? sets[0] : 'mixed';
  }, [tickedRows, managersOf]);

  const anyTicked = tickedRows.length > 0;
  const openAssign = useCallback(() => { if (anyTicked) setAssigning(true); }, [anyTicked]);
  const closeAssign = useCallback(() => setAssigning(false), []);
  const saved = useCallback((next: StoreVacancyManagersResponse | null) => {
    setAssigning(false);
    setTicks(NO_TICKS);
    // Whoever just saved may assign: the tick boxes stay even if this answer leaves that out.
    if (next && projectId) setLoad({ projectId, answer: { ...next, canManage: true } });
    // Saved, but the answer could not be read: ask for the assignments again.
    else setReads((n) => n + 1);
  }, [projectId]);

  return {
    known: answer !== null && today,
    managersOf,
    managerOf,
    managerApplies,
    canAssign,
    isTicked,
    toggle,
    vacantCount: vacantRows.length,
    tickedCount: tickedRows.length,
    toggleAll,
    tickedStoreIds,
    tickedHaveManager,
    tickedCurrent,
    assigning: assigning && anyTicked,
    openAssign,
    closeAssign,
    saved,
  };
}

/** What the cell says, on hover, about a manager on record for a store that is not Active. */
export const NOT_APPLIED_TITLE = 'Only an Active store shows in its manager’s lists. This store is not Active, so its manager does not see it.';

const KIND_LABEL: Record<VacancyRoleKind, string> = { trade_head: 'Trade Head', am: 'AM' };

/**
 * Everyone a store lies under, for the hover: one line per list ("Trade Head: …", "AM: …",
 * anyone else under "Other"), a leaver marked "(left)".
 */
export const managersTitle = (managers: readonly StoreVacancyManager[]): string => {
  const lines = new Map<string, string[]>();
  for (const m of [...managers].sort(byKindThenName)) {
    const label = m.managerKind ? KIND_LABEL[m.managerKind] : 'Other';
    const list = lines.get(label) ?? [];
    list.push(m.managerActive ? m.managerName : `${m.managerName} (left)`);
    lines.set(label, list);
  }
  return [...lines].map(([label, names]) => `${label}: ${names.join(', ')}`).join('\n');
};

/** The Manager cell of one row: the first person, "+N" for the others, everyone on hover. */
export function StoreManagerCell({ known, understaffed, managers, applies = true }: {
  known: boolean;
  understaffed: boolean;
  /** The people the row lies under (managersOf), Trade Heads first. */
  managers: readonly StoreVacancyManager[];
  /** The managers have the store in their lists (managerApplies). False: on record only. */
  applies?: boolean;
}) {
  if (!known) return <span className="text-gray-400">—</span>;
  if (understaffed) {
    return (
      <span className="text-gray-500" title="Someone is deployed here, so the store follows their manager">
        By its team
      </span>
    );
  }
  const [first] = managers;
  if (!first) return <span className="text-gray-500">Not set</span>;
  const more = managers.length - 1;
  const others = more > 0 ? <span className="ml-1 text-xs font-medium text-gray-500">+{more}</span> : null;
  if (!applies) {
    return (
      <div title={more > 0 ? `${NOT_APPLIED_TITLE}\n\n${managersTitle(managers)}` : NOT_APPLIED_TITLE}>
        <div className="text-gray-500">{first.managerName}{others}</div>
        <div className="text-xs text-amber-600">Not applied</div>
      </div>
    );
  }
  if (more === 0) {
    return (
      <>
        {first.managerActive ? (
          <div className="text-gray-900">{first.managerName}</div>
        ) : (
          <div className="text-amber-600" title="No longer a current employee: assign another manager">
            {first.managerName}
          </div>
        )}
        {first.managerRole && <div className="text-xs text-gray-400">{first.managerRole}</div>}
      </>
    );
  }
  return (
    <div title={managersTitle(managers)}>
      <div className={first.managerActive ? 'text-gray-900' : 'text-amber-600'}>{first.managerName}{others}</div>
      {first.managerRole && <div className="text-xs text-gray-400">{first.managerRole}</div>}
    </div>
  );
}

const TICK_BOX = 'h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500';

/** The header box: ticks every vacant row listed, or clears them when all are ticked. */
export function TickAllVacantStores({ list }: { list: VacancyManagerList }) {
  const all = list.vacantCount > 0 && list.tickedCount === list.vacantCount;
  const some = list.tickedCount > 0 && !all;
  const box = useRef<HTMLInputElement>(null);
  // "Some, not all" has no attribute: it can only be set on the element.
  useEffect(() => {
    if (box.current) box.current.indeterminate = some;
  }, [some]);
  return (
    <input
      ref={box}
      type="checkbox"
      aria-label="Select all vacant stores"
      checked={all}
      disabled={list.vacantCount === 0}
      onChange={list.toggleAll}
      className={TICK_BOX}
    />
  );
}

/** The box on one vacant row. */
export function TickVacantStore({ list, row }: { list: VacancyManagerList; row: VacancyListRow }) {
  return (
    <input
      type="checkbox"
      aria-label={`Select ${row.name}`}
      checked={list.isTicked(row)}
      onChange={() => list.toggle(row)}
      className={TICK_BOX}
    />
  );
}

/** "Assign manager", with the number of stores ticked. Drawn like the Download pill beside it. */
export function AssignManagerButton({ list }: { list: VacancyManagerList }) {
  if (!list.canAssign) return null;
  return (
    <button
      type="button"
      onClick={list.openAssign}
      disabled={list.tickedCount === 0}
      className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <UserCog className="h-3.5 w-3.5" />
      Assign manager{list.tickedCount > 0 ? ` (${list.tickedCount})` : ''}
    </button>
  );
}
